/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { Button, Tag, Typography2 } from '@coursera/cds-core';

import ToolsDialog from 'bundles/enterprise-learning-path-description-page/components/product-overview/ToolsDialog';
import TrackedButton from 'bundles/page/components/TrackedButton';
import Subsection from 'bundles/unified-description-page-common/components/shared/Subsection';

import _t from 'i18n!nls/enterprise-learning-path-description-page';

const styles = {
  container: css`
    display: flex;
    flex-wrap: wrap;
    gap: var(--cds-spacing-100);
  `,
  button: css`
    padding: 0;
    margin-left: 0;
    text-decoration: underline;

    :hover {
      background: transparent;
      text-decoration: none;
    }
  `,
  tagOverrides: css`
    padding: var(--cds-spacing-50) var(--cds-spacing-150);
    border-radius: var(--cds-border-radius-max);
  `,
  titleOverride: css`
    margin-bottom: calc(-1 * var(--cds-spacing-100));
  `,
};

type Props = {
  tools: string[];
};

const MAX_TOOLS_TO_SHOW = 5;

const ToolsSection = ({ tools }: Props) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const maxToolsToShow = MAX_TOOLS_TO_SHOW;
  if (!tools?.length) {
    return null;
  }

  const toolsToShow = tools.slice(0, maxToolsToShow);
  const hasMoreTools = tools.length > maxToolsToShow;

  const openDialog = () => setIsDialogOpen(true);
  const closeDialog = () => setIsDialogOpen(false);

  return (
    <React.Fragment>
      <Subsection
        title={
          <Typography2 component="h2" variant="subtitleMedium" css={styles.titleOverride}>
            {_t("Tools you'll use")}
          </Typography2>
        }
        spacing={32}
      >
        <ul css={styles.container}>
          {toolsToShow.map((tool) => (
            <li key={`tool-${tool}`}>
              <span>
                <Tag css={styles.tagOverrides}>{tool}</Tag>
              </span>
            </li>
          ))}
          {hasMoreTools && (
            <li>
              <Button
                variant="ghostSecondary"
                edgeAlign="start"
                size="small"
                onClick={openDialog}
                css={styles.button}
                component={TrackedButton}
                trackingName="overview_tools_toggle"
                requireFullyVisible={false}
                withVisibilityTracking={false}
              >
                {_t('Show all')}
              </Button>
            </li>
          )}
        </ul>
      </Subsection>
      <ToolsDialog open={isDialogOpen} onClose={closeDialog} tools={tools} />
    </React.Fragment>
  );
};

export default ToolsSection;
